import { useState, useEffect } from 'react';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { getTenantW2Employees, getTenantContractors } from '../../services/crmService';
import { db, storage } from '../../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth'; // <-- ADDED FOR SECURE RE-AUTH

export default function HRDocsVault() {
  const { tenantId, loading: tenantLoading } = useTenant();
  const { currentUser } = useAuth();
  
  // --- ADVANCED SECURITY: STEP-UP AUTHENTICATION (FIREBASE RE-AUTH) ---
  const [isVaultUnlocked, setIsVaultUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const [activeTab, setActiveTab] = useState('w2');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  
  const [w2Staff, setW2Staff] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(null);

  useEffect(() => {
    const fetchPersonnel = async () => {
      // Prevent fetching sensitive data until the vault is cryptographically unlocked
      if (tenantLoading || !tenantId || !isVaultUnlocked) return;
      setIsLoading(true);
      try {
        const [w2Data, contractorData] = await Promise.all([
          getTenantW2Employees(tenantId),
          getTenantContractors(tenantId)
        ]);
        setW2Staff(w2Data);
        setContractors(contractorData);
      } catch (error) {
        console.error("Failed to fetch HR vault data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPersonnel();
  }, [tenantId, tenantLoading, isVaultUnlocked]);

  // --- FIREBASE RE-AUTHENTICATION LOGIC ---
  const verifyPassword = async (e) => {
    e.preventDefault();
    if (!passwordInput || !currentUser) return;
    
    setIsAuthenticating(true);
    setAuthError('');

    try {
      // Generate a fresh credential using the user's active email and the provided password
      const credential = EmailAuthProvider.credential(currentUser.email, passwordInput);
      
      // Ping Firebase to cryptographically verify the credential
      await reauthenticateWithCredential(currentUser, credential);
      
      // If successful, unlock the vault and clear the password from state
      setIsVaultUnlocked(true);
      setPasswordInput('');
      
      // Auto-lock the vault after 15 minutes of inactivity for compliance
      setTimeout(() => {
        setIsVaultUnlocked(false);
        setSelectedPerson(null);
      }, 15 * 60 * 1000); 

    } catch (error) {
      console.error("Vault Authentication Failed:", error);
      setAuthError('Authentication failed. Please verify your password.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const activeData = activeTab === 'w2' ? w2Staff : contractors;
  const filteredData = activeData.filter(person => 
    (person.firstName + ' ' + person.lastName).toLowerCase().includes(searchQuery.toLowerCase()) ||
    person.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // --- DYNAMIC COMPLIANCE DOCUMENT MATRIX ---
  const getRequiredDocs = () => {
    if (activeTab === 'w2') {
      return [
        { key: 'i9', label: 'I-9 Form & ID' },
        { key: 'w4', label: 'W-4 Tax Elections' },
        { key: 'ach', label: 'Direct Deposit Auth' },
        { key: 'pfml', label: 'WA PFML Signatures' },
        { key: 'handbook', label: 'Employee Handbook' }
      ];
    } else {
      const baseDocs = [
        { key: 'w9', label: 'W-9 Tax Form' },
        { key: 'ica', label: 'Independent Auth (ICA)' },
        { key: 'nda', label: 'Non-Disclosure (NDA)' },
        { key: 'techLicense', label: 'Technology License' },
        { key: 'autonomy', label: 'Autonomy Clause' },
        { key: 'coi', label: 'Certificate of Insurance (COI)' },
        { key: 'entity', label: 'Proof of Business Entity' }
      ];
      
      if (selectedPerson?.operatingState?.toUpperCase() === 'AZ') {
        baseDocs.push({ key: 'azDibs', label: 'AZ DIBS Form' });
      }
      
      return baseDocs;
    }
  };

  const requiredDocs = selectedPerson ? getRequiredDocs() : [];

  const getFirestorePath = (docKey) => {
    const legalKeys = ['ica', 'nda', 'techLicense', 'autonomy', 'azDibs', 'handbook', 'pfml'];
    const assetKeys = ['coi', 'entity', 'w9', 'i9', 'w4', 'ach'];

    if (legalKeys.includes(docKey)) return `secureVault.legal.${docKey}Acknowledged`;
    if (assetKeys.includes(docKey)) return `secureVault.assets.${docKey}DocumentUrl`;
    return `secureVault.assets.${docKey}`;
  };

  const getVaultedUrl = (docKey, person) => {
    if (!person?.secureVault) return null;
    const legalKeys = ['ica', 'nda', 'techLicense', 'autonomy', 'azDibs', 'handbook', 'pfml'];
    
    if (legalKeys.includes(docKey)) {
      const val = person.secureVault.legal?.[`${docKey}Acknowledged`];
      return (val && val !== 'verified_offline' && typeof val === 'string') ? val : null;
    }
    return person.secureVault.assets?.[`${docKey}DocumentUrl`] || null;
  };

  const handleFileUpload = async (e, docKey) => {
    const file = e.target.files[0];
    if (!file || !selectedPerson || !tenantId) return;

    setIsUploading(true);
    
    try {
      if (currentUser) {
        await currentUser.getIdToken(true);
      }

      const fileExtension = file.name.split('.').pop();
      const storagePath = `compliance_vault/${tenantId}/${selectedPerson.id}/${docKey}_MANUAL_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadURL = await getDownloadURL(storageRef);

      const collectionName = activeTab === 'w2' ? 'crm_employees' : 'crm_contractors';
      const personRef = doc(db, 'clients', tenantId, collectionName, selectedPerson.id);
      
      const dbPath = getFirestorePath(docKey);
      
      await updateDoc(personRef, {
        [dbPath]: downloadURL
      });

      setSelectedPerson(prev => {
        if (!prev) return prev;
        const newVault = prev.secureVault ? JSON.parse(JSON.stringify(prev.secureVault)) : { legal: {}, assets: {} };
        
        if (!newVault.legal) newVault.legal = {};
        if (!newVault.assets) newVault.assets = {};

        if (['ica', 'nda', 'techLicense', 'autonomy', 'azDibs', 'handbook', 'pfml'].includes(docKey)) {
          newVault.legal[`${docKey}Acknowledged`] = downloadURL;
        } else {
          newVault.assets[`${docKey}DocumentUrl`] = downloadURL;
        }
        
        return { ...prev, secureVault: newVault };
      });

    } catch (error) {
      console.error("Secure upload failed:", error);
      alert("System Error: Failed to vault the document. Check console.");
    } finally {
      setIsUploading(false);
      e.target.value = null; 
    }
  };

  if (tenantLoading) return <div className="p-12 flex justify-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  // --- RENDER: VAULT LOCK GATEKEEPER ---
  if (!isVaultUnlocked) {
    return (
      <div className="h-[calc(100vh-120px)] flex flex-col items-center justify-center animate-in fade-in duration-500">
        <div className="bg-white border border-slate-300 shadow-2xl p-10 max-w-md w-full rounded-sm text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500"></div>
          
          <div className="w-20 h-20 bg-slate-50 border-2 border-slate-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <i className="fa-solid fa-lock text-3xl text-slate-400"></i>
          </div>
          
          <h2 className="text-xl font-black text-slate-900 uppercase tracking-widest mb-2">Zero-Trust Vault</h2>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-6 leading-relaxed">
            Highly sensitive PII detected. Step-up authentication required to decrypt personnel files.
          </p>

          <form onSubmit={verifyPassword} className="space-y-5">
            <div className="text-left">
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                Re-Authenticate Session
              </label>
              <div className="relative">
                <i className="fa-solid fa-key absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                <input
                  type="password"
                  placeholder="Enter your account password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    setAuthError('');
                  }}
                  className={`w-full pl-10 pr-4 py-3 bg-slate-50 border-2 rounded-sm text-sm font-bold focus:outline-none transition-colors ${
                    authError ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-slate-300 focus:border-emerald-500 text-slate-900'
                  }`}
                />
              </div>
              {authError && (
                <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2">
                  <i className="fa-solid fa-triangle-exclamation mr-1"></i> {authError}
                </p>
              )}
            </div>

            <button 
              type="submit" 
              disabled={isAuthenticating || !passwordInput}
              className="w-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white font-black text-[11px] uppercase tracking-widest py-4 transition-colors shadow-sm rounded-sm flex items-center justify-center gap-3"
            >
              {isAuthenticating ? (
                <><i className="fa-solid fa-spinner fa-spin"></i> Verifying...</>
              ) : (
                <><i className="fa-solid fa-unlock"></i> Decrypt & Access Vault</>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- RENDER: MAIN VAULT UI ---
  return (
    <div className="animate-in fade-in duration-500 pb-24 relative h-full flex flex-col">
      
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase flex items-center gap-3">
            HR & Docs Vault <i className="fa-solid fa-lock-open text-emerald-500 text-lg"></i>
          </h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Securely manage sensitive compliance documents, W-9s, I-9s, and identity verification.</p>
        </div>
        <button 
          onClick={() => { setIsVaultUnlocked(false); setPasswordInput(''); setSelectedPerson(null); }}
          className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-600 text-[9px] font-black uppercase tracking-widest px-4 py-2 transition-colors rounded-sm shadow-sm"
        >
          <i className="fa-solid fa-lock mr-2"></i> Lock Vault
        </button>
      </div>

      <div className="flex border-b border-slate-300 mb-6 shrink-0 bg-white">
        <button 
          onClick={() => { setActiveTab('w2'); setSelectedPerson(null); }} 
          className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'w2' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
        >
          <i className="fa-solid fa-id-badge mr-2"></i> W-2 Employee Files
        </button>
        <button 
          onClick={() => { setActiveTab('contractors'); setSelectedPerson(null); }} 
          className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-b-2 ${activeTab === 'contractors' ? 'border-emerald-500 text-emerald-600 bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
        >
          <i className="fa-solid fa-users-gear mr-2"></i> 1099 Contractor Files
        </button>
      </div>

      <div className="flex flex-1 gap-6 min-h-0">
        <div className="w-1/3 bg-white border border-slate-300 shadow-sm flex flex-col min-h-0">
          <div className="p-3 border-b border-slate-200 bg-slate-50 relative">
            <i className="fa-solid fa-magnifying-glass absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]"></i>
            <input 
              type="text" 
              placeholder="SEARCH PERSONNEL..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-[10px] font-bold text-slate-800 border border-slate-300 uppercase tracking-widest focus:outline-none focus:border-emerald-500 placeholder:text-slate-400" 
            />
          </div>
          
          <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-slate-100">
            {isLoading ? (
              <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div></div>
            ) : filteredData.length === 0 ? (
              <div className="p-8 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">No personnel found.</div>
            ) : (
              filteredData.map(person => (
                <button 
                  key={person.id}
                  onClick={() => setSelectedPerson(person)}
                  className={`w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-center gap-3 ${selectedPerson?.id === person.id ? 'bg-emerald-50/50 border-l-2 border-l-emerald-500' : 'border-l-2 border-l-transparent'}`}
                >
                  <div className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center font-black text-xs shrink-0">
                    {(person.firstName?.[0] || '') + (person.lastName?.[0] || '')}
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-xs font-bold text-slate-900 truncate">{person.firstName} {person.lastName}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate">{person.email}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex-1 bg-white border border-slate-300 shadow-sm flex flex-col min-h-0 relative">
          
          {isUploading && (
            <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Encrypting & Vaulting File...</p>
            </div>
          )}

          {!selectedPerson ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-12 bg-slate-50">
              <i className="fa-solid fa-folder-closed text-4xl text-slate-300 mb-4"></i>
              <h2 className="text-sm font-black text-slate-800 uppercase tracking-widest">Select Personnel</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">Choose a staff member to view or upload compliance documents.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
              <div className="flex justify-between items-start border-b border-slate-200 pb-6 mb-8">
                <div>
                  <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">{selectedPerson.firstName} {selectedPerson.lastName}</h2>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                    ID: {selectedPerson.id} • {selectedPerson.role || selectedPerson.commissionTier} 
                    {selectedPerson.operatingState && ` • State: ${selectedPerson.operatingState.toUpperCase()}`}
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 text-[9px] font-bold uppercase tracking-widest flex items-center gap-2">
                  <i className="fa-solid fa-shield-halved"></i> Admin Access Level Required
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-[11px] font-black text-slate-800 uppercase tracking-widest border-b border-slate-200 pb-2">Required Compliance Files</h3>
                
                {requiredDocs.map((doc) => {
                  const fileUrl = getVaultedUrl(doc.key, selectedPerson);
                  
                  const legalKeys = ['ica', 'nda', 'techLicense', 'autonomy', 'azDibs', 'handbook', 'pfml'];
                  const isOfflineVerified = legalKeys.includes(doc.key) && selectedPerson.secureVault?.legal?.[`${doc.key}Acknowledged`] === 'verified_offline';
                  const isLegacyOffline = selectedPerson.offlineDocs?.[doc.key] === true;
                  
                  const isUploaded = !!fileUrl || isOfflineVerified || isLegacyOffline;

                  return (
                    <div key={doc.key} className={`flex items-center justify-between p-4 border transition-colors ${isUploaded ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 border flex items-center justify-center ${isUploaded ? 'bg-emerald-100 border-emerald-200 text-emerald-600' : 'bg-white border-slate-200 text-slate-400'}`}>
                          <i className={`fa-solid ${isUploaded ? 'fa-file-circle-check' : 'fa-file-pdf'}`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900">{doc.label}</p>
                          {isUploaded ? (
                            <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest mt-0.5">
                              {(isOfflineVerified || isLegacyOffline) ? 'Verified Offline' : 'Securely Vaulted'}
                            </p>
                          ) : (
                            <p className="text-[9px] font-bold text-amber-500 uppercase tracking-widest mt-0.5">Missing / Awaiting Upload</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="bg-white border border-slate-300 hover:border-emerald-500 hover:text-emerald-600 text-slate-600 cursor-pointer text-[9px] font-bold px-4 py-2 uppercase tracking-widest transition-colors">
                          <i className="fa-solid fa-cloud-arrow-up mr-2"></i> {isUploaded && !(isOfflineVerified || isLegacyOffline) ? 'Replace' : 'Upload'}
                          <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, doc.key)} accept=".pdf,.png,.jpg,.jpeg" />
                        </label>
                        {fileUrl ? (
                          <button onClick={() => window.open(fileUrl, '_blank')} className="bg-slate-900 hover:bg-slate-800 text-white text-[9px] font-bold px-4 py-2 uppercase tracking-widest transition-colors shadow-sm">
                            <i className="fa-solid fa-eye mr-2"></i> View
                          </button>
                        ) : (
                          <button disabled className="bg-slate-200 text-slate-400 text-[9px] font-bold px-4 py-2 uppercase tracking-widest opacity-50 cursor-not-allowed">
                            <i className="fa-solid fa-eye mr-2"></i> View
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}